/**
 * Product Details Page - Detailed view of a single product
 */

import { ContentBlock, Box, IconHeart } from 'braid-design-system';
import { useState } from 'react';
import type React from 'react';
import { useParams, useNavigate } from 'react-router';

import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { PageHead } from '../../components/PageHead/PageHead';
import { ProductGrid } from '../../components/ProductGrid/ProductGrid';
import { ShareButtons } from '../../components/ShareButtons/ShareButtons';
import {
  buildBreadcrumbJsonLd,
  buildProductJsonLd,
} from '../../constants/jsonLd';
import { SITE_ORIGIN } from '../../constants/site';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { getProductById, getRelatedProducts } from '../../data/productCatalog';
import type { ProductVariant } from '../../types';

import * as styles from './ProductDetails.css';

export const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    null,
  );
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToCart } = useCart();

  const product = id ? getProductById(id) : undefined;
  const hasMultipleImages = product && product.images.length > 1;
  const relatedProducts = product ? getRelatedProducts(product) : [];
  const inWishlist = product ? isInWishlist(product.id) : false;
  const activeVariant = selectedVariant ?? product?.variants?.[0] ?? null;
  const displayPrice = activeVariant?.price ?? product?.price ?? 0;

  const handlePreviousImage = () => {
    if (!product) {
      return;
    }
    setSelectedImageIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1,
    );
  };

  const handleNextImage = () => {
    if (!product) {
      return;
    }
    setSelectedImageIndex((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!hasMultipleImages) {
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePreviousImage();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNextImage();
    }
  };

  const handleWishlistToggle = () => {
    if (!product) {
      return;
    }
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const handleSaveForLater = () => {
    if (!product) {
      return;
    }
    addToCart(product, 1);
  };

  const productUrl = product
    ? `${SITE_ORIGIN}/products/${product.id}`
    : SITE_ORIGIN;
  const productImage = product?.images[0];

  const breadcrumbJsonLd = product ? buildBreadcrumbJsonLd(product) : null;
  const productJsonLd = product
    ? buildProductJsonLd(product, displayPrice)
    : null;

  if (!product) {
    return (
      <ContentBlock width="large">
        <Box paddingY="large">
          <PageHead
            title="Product not found"
            description="The requested product could not be found."
            path="/products"
          />
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Product not found</h1>
        </Box>
      </ContentBlock>
    );
  }

  return (
    <ContentBlock width="large">
      <PageHead
        title={product.name}
        description={product.description}
        path={`/products/${product.id}`}
        image={productImage}
        jsonLd={
          breadcrumbJsonLd && productJsonLd
            ? [breadcrumbJsonLd, productJsonLd]
            : undefined
        }
      />
      <Box paddingY="large">
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Products', to: '/products' },
            {
              label: product.category,
              to: `/products?category=${encodeURIComponent(product.category)}`,
            },
          ]}
        />

        <div className={styles.content}>
          <div className={styles.imageSection}>
            {hasMultipleImages && (
              <div className={styles.thumbnailStack}>
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    className={`${styles.thumbnailButton} ${
                      index === selectedImageIndex ? styles.thumbnailActive : ''
                    }`}
                    onClick={() => setSelectedImageIndex(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - view ${index + 1}`}
                      className={styles.thumbnailImage}
                    />
                  </button>
                ))}
              </div>
            )}
            <div
              className={styles.mainImageContainer}
              onKeyDown={handleKeyDown}
              tabIndex={hasMultipleImages ? 0 : -1}
              role={hasMultipleImages ? 'region' : undefined}
              aria-label={
                hasMultipleImages
                  ? `Product images, image ${selectedImageIndex + 1} of ${product.images.length}`
                  : undefined
              }
            >
              <img
                src={product.images[selectedImageIndex]}
                alt={`${product.name} - view ${selectedImageIndex + 1}`}
                className={styles.image}
              />
              {hasMultipleImages && (
                <>
                  <button
                    className={styles.imageNavButton}
                    style={{ left: '10px' }}
                    onClick={handlePreviousImage}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    className={styles.imageNavButton}
                    style={{ right: '10px' }}
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </>
              )}
              {!product.inStock && (
                <div className={styles.outOfStockBadge}>Out of Stock</div>
              )}
            </div>
          </div>

          <div className={styles.detailsSection}>
            <div className={styles.category}>{product.category}</div>
            <h1 className={styles.title}>{product.name}</h1>

            <p className={styles.description}>{product.description}</p>

            {/* {product.variants && product.variants.length > 0 && (
              <div className={styles.quantitySection}>
                <span className={styles.quantityLabel}>Format:</span>
                <div className={styles.stlLinksGrid}>
                  {product.variants.map((variant) => (
                    <button
                      key={variant.id}
                      type="button"
                      className={`${styles.stlLinkButton} ${activeVariant?.id === variant.id
                        ? styles.thumbnailActive
                        : ''
                        }`}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      {variant.label} — ${variant.price.toFixed(2)}
                    </button>
                  ))}
                </div>
              </div>
            )} */}

            <Box className={styles.quantitySection} background="customDark">
              <button
                type="button"
                className={styles.stlLinkButton}
                onClick={handleWishlistToggle}
              >
                {inWishlist ? (
                  <>
                    <IconHeart active /> Saved to wishlist
                  </>
                ) : (
                  <>
                    <IconHeart /> Add to wishlist
                  </>
                )}
              </button>
            </Box>

            {product.stlLinks &&
              Object.entries(product.stlLinks).some(([, url]) => url) && (
                <div className={styles.stlLinksSection}>
                  <h3 className={styles.stlLinksTitle}>Have a 3D printer?</h3>
                  <p>
                    The STL files are available for purchase on the following
                    sites:
                  </p>
                  <div className={styles.stlLinksGrid}>
                    {product.stlLinks.myminifactory && (
                      <a
                        href={product.stlLinks.myminifactory}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.stlLinkButton}
                      >
                        MyMiniFactory
                      </a>
                    )}
                    {product.stlLinks.cults && (
                      <a
                        href={product.stlLinks.cults}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.stlLinkButton}
                      >
                        Cults3D
                      </a>
                    )}
                    {product.stlLinks.makerworld && (
                      <a
                        href={product.stlLinks.makerworld}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.stlLinkButton}
                      >
                        MakerWorld
                      </a>
                    )}
                    {product.stlLinks.thingiverse && (
                      <a
                        href={product.stlLinks.thingiverse}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.stlLinkButton}
                      >
                        Thingiverse
                      </a>
                    )}
                  </div>
                </div>
              )}

            <ShareButtons
              url={productUrl}
              title={product.name}
              image={productImage}
            />

            {!product.inStock && (
              <div className={styles.outOfStockBadge}>Out of Stock</div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <Box paddingY="large">
            <h2>You might also like</h2>
            <ProductGrid products={relatedProducts} />
          </Box>
        )}
      </Box>
    </ContentBlock>
  );
};
